import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import * as htmlToImage from 'html-to-image';
import { FlyerCanvas, FLYER_TEMPLATES, FLYER_W, FLYER_H } from '../../components/flyer/FlyerTemplates';
import { makeQrDataUrl } from '../../lib/qr';
import { CalendarRange, Sparkles, Loader2, Copy, FileVideo, FileText, Trash2, ChevronDown, ChevronRight, Shuffle, Minimize2, Send, X, Check, DollarSign, UserPlus, Image as ImageIcon, Video, Download, RefreshCw, AlertTriangle, Wand2, Square, Play, KeyRound, LayoutTemplate } from 'lucide-react';

/**
 * Done-For-You Content Plan.
 * Builds a {days}-day skeleton: {videos_per_day} short-form video scripts +
 * {posts_per_day} posts per day, each on a DISTINCT theme, grounded in the brand
 * reference library. Filled in small batches with a live progress bar.
 * Per item: Copy, Remix (with optional guidance), and for POSTS a one-click Short version.
 * A Both/Videos/Posts filter lets you scroll just videos or just posts.
 */
/* Extract a human-readable error message (handles string OR {message} detail). */
const apiErr = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (!d) return fallback;
  return typeof d === 'string' ? d : (d.message || fallback);
};


/* ---------- BYOK: bring-your-own provider keys ---------- */
function ByokModal({ api, onClose }) {
  const [status, setStatus] = useState(null);
  const [openaiKey, setOpenaiKey] = useState('');
  const [geminiKey, setGeminiKey] = useState('');
  const [saving, setSaving] = useState('');

  useEffect(() => {
    api.get('/api/studio/byok').then((r) => setStatus(r.data)).catch(() => setStatus({}));
  }, [api]);

  const save = async (provider) => {
    const key = provider === 'openai' ? openaiKey.trim() : geminiKey.trim();
    if (!key) { toast.error('Paste a key first'); return; }
    setSaving(provider);
    try {
      const body = provider === 'openai' ? { openai_key: key } : { gemini_key: key };
      const r = await api.post('/api/studio/byok', body);
      setStatus(r.data.status);
      if (provider === 'openai') setOpenaiKey(''); else setGeminiKey('');
      toast.success(`${provider === 'openai' ? 'OpenAI' : 'Gemini'} key saved — unlimited generations on your account`);
    } catch (e) {
      toast.error(apiErr(e, 'Could not save key'));
    } finally { setSaving(''); }
  };

  const clear = async (provider) => {
    setSaving(provider);
    try {
      const r = await api.delete(`/api/studio/byok/${provider}`);
      setStatus(r.data.status);
      toast.message(`${provider === 'openai' ? 'OpenAI' : 'Gemini'} key removed`);
    } catch { toast.error('Could not remove key'); } finally { setSaving(''); }
  };

  const Row = ({ provider, label, help, value, setValue, placeholder }) => {
    const s = status?.[provider] || {};
    return (
      <div className="rounded-xl border border-[#E2E8F0] p-3.5 mb-3" data-testid={`byok-row-${provider}`}>
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-bold text-[#0A0F1C]">{label}</span>
          {s.set ? (
            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600" data-testid={`byok-set-${provider}`}><Check size={12} aria-hidden="true" /> Connected {s.masked}</span>
          ) : (
            <span className="text-[11px] text-[#94A3B8]">Not connected</span>
          )}
        </div>
        <p className="text-[11px] text-[#64748B] mb-2">{help}</p>
        <div className="flex gap-2">
          <input type="password" value={value} onChange={(e) => setValue(e.target.value)} placeholder={placeholder}
            className="flex-1 px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm" data-testid={`byok-input-${provider}`} autoComplete="off" />
          <button type="button" onClick={() => save(provider)} disabled={saving === provider}
            className="px-3 py-2 rounded-lg bg-[#0A0F1C] text-white text-xs font-bold hover:bg-black disabled:opacity-60" data-testid={`byok-save-${provider}`}>
            {saving === provider ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : 'Save'}
          </button>
          {s.set && (
            <button type="button" onClick={() => clear(provider)} disabled={saving === provider}
              className="px-3 py-2 rounded-lg border border-[#E2E8F0] text-xs font-bold text-[#DC2626] hover:bg-red-50" data-testid={`byok-clear-${provider}`}>Remove</button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="byok-modal" onClick={onClose}>
      <div className="bg-white text-[#0A0F1C] rounded-2xl w-full max-w-lg p-6 shadow-xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-1">
          <h3 className="text-lg font-bold inline-flex items-center gap-2"><KeyRound size={18} className="text-[#0EA5E9]" aria-hidden="true" /> Your API keys (BYOK)</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="text-[#94A3B8] hover:text-[#0A0F1C]"><X size={18} aria-hidden="true" /></button>
        </div>
        <p className="text-sm text-[#475569] mb-4">
          Add your own provider keys to get <strong>unlimited</strong> generations billed directly to your account (bypasses daily limits).
          Leave blank to keep using the shared plan with your daily quota.
        </p>
        <Row provider="gemini" label="Google Gemini — images" value={geminiKey} setValue={setGeminiKey}
          placeholder="AIza…"
          help="Used for AI post images (Nano Banana). Get a key at aistudio.google.com → API keys." />
        <Row provider="openai" label="OpenAI — videos" value={openaiKey} setValue={setOpenaiKey}
          placeholder="sk-…"
          help="Used for AI Sora 2 videos. Get a key at platform.openai.com → API keys (Sora access required)." />
        <p className="text-[11px] text-[#94A3B8] mt-1">Keys are stored securely to make your generations and can be removed anytime.</p>
      </div>
    </div>
  );
}


/* ---------- Branded Flyer composer for a single post ---------- */
function PostFlyerModal({ api, plan, item, onClose, onAttached }) {
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState('');
  const [loading, setLoading] = useState(true);
  const [copy, setCopy] = useState(null);
  const [bgUrl, setBgUrl] = useState('');
  const [bgStyle, setBgStyle] = useState('photorealistic');
  const [templateId, setTemplateId] = useState(FLYER_TEMPLATES[0].id);
  const [qr, setQr] = useState({ enabled: false, url: '', position: 'bottom-right', size: 200, label: 'Scan me', dataUrl: '' });
  const [busy, setBusy] = useState('');
  const exportRef = useRef(null);
  const postText = item.full_post || item.video_script || '';

  useEffect(() => {
    if (!qr.enabled || !qr.url) { if (qr.dataUrl) setQr((s) => ({ ...s, dataUrl: '' })); return; }
    let cancel = false;
    makeQrDataUrl(qr.url, '#1E5BA8').then((d) => { if (!cancel) setQr((s) => ({ ...s, dataUrl: d })); });
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qr.enabled, qr.url]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    api.get('/api/flyer-studio/brands').then((r) => {
      const bs = r.data.brands || [];
      setBrands(bs);
      const def = bs.find((b) => b.is_default) || bs[0];
      if (def) setBrandId(def.brand_id);
    }).catch(() => setBrands([])).finally(() => setLoading(false));
  }, [api]);

  useEffect(() => {
    const firstSentence = (postText.split(/(?<=[.!?])\s/)[0] || postText).slice(0, 160);
    setCopy({
      headline: (item.title || item.theme || 'Protect What Matters').slice(0, 60),
      subhead: '', body: firstSentence,
      cta: 'Call or text today for a FREE quote!',
      coverage_highlights: [], theme_name: item.pillar || '',
    });
  }, [item, postText]);

  const brand = brands.find((b) => b.brand_id === brandId);

  const aiCopy = async () => {
    if (!brand) return;
    setBusy('copy');
    try {
      const r = await api.post('/api/flyer-studio/generate-copy', {
        brand_id: brand.brand_id, theme_text: item.title || item.theme || 'Daily flyer',
        extra_instructions: `Base the flyer on this social post: ${postText.slice(0, 600)}`,
      });
      setCopy(r.data);
      toast.success('Flyer copy ready');
    } catch (e) { toast.error(apiErr(e, 'Copy failed')); } finally { setBusy(''); }
  };

  const genBg = async () => {
    if (!brand) return;
    setBusy('bg');
    try {
      const prompt = copy?.background_prompt || item.title || 'warm trustworthy protective scene';
      const r = await api.post('/api/flyer-studio/generate-background', {
        brand_id: brand.brand_id, background_prompt: prompt, aspect: 'portrait', style: bgStyle, count: 1,
      });
      setBgUrl(r.data.background_url);
      toast.success('Background ready');
    } catch (e) { toast.error(apiErr(e, 'Background failed')); } finally { setBusy(''); }
  };

  const attach = async () => {
    if (!copy || !brand) return;
    setBusy('attach');
    try {
      await new Promise((res) => setTimeout(res, 300));
      const dataUrl = await htmlToImage.toPng(exportRef.current, { width: FLYER_W, height: FLYER_H, pixelRatio: 1, cacheBust: true });
      const r = await api.post('/api/flyer-studio/attach-to-plan-item', {
        plan_id: plan.plan_id, item_id: item.item_id, data_url: dataUrl, background_url: bgUrl,
      });
      onAttached(item.item_id, r.data.image_url);
      toast.success('Branded flyer attached to this post');
      onClose();
    } catch (e) { toast.error(apiErr(e, 'Could not attach flyer')); } finally { setBusy(''); }
  };

  const scale = 300 / FLYER_W;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="dfy-flyer-modal" onClick={onClose}>
      <div className="bg-white text-[#0A0F1C] rounded-2xl w-full max-w-4xl p-6 shadow-xl max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-lg font-bold inline-flex items-center gap-2"><LayoutTemplate size={18} className="text-[#1E5BA8]" aria-hidden="true" /> Branded flyer for this post</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="text-[#94A3B8] hover:text-[#0A0F1C]"><X size={18} aria-hidden="true" /></button>
        </div>
        {loading ? (
          <div className="py-16 flex items-center justify-center text-[#94A3B8]"><Loader2 size={22} className="animate-spin" aria-hidden="true" /></div>
        ) : brands.length === 0 ? (
          <div className="py-12 text-center" data-testid="dfy-flyer-nobrand">
            <p className="text-sm text-[#475569]">Set up your Flyer Brand Kit first (logo, headshot, contact info).</p>
            <a href="/admin/flyer-studio" className="inline-block mt-3 px-4 py-2 rounded-lg bg-[#1E5BA8] text-white text-sm font-bold">Open Flyer Studio</a>
          </div>
        ) : (
          <div className="grid md:grid-cols-[1fr_320px] gap-5">
            <div className="space-y-3">
              {brands.length > 1 && (
                <select value={brandId} onChange={(e) => setBrandId(e.target.value)} data-testid="dfy-flyer-brand" className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm font-semibold">
                  {brands.map((b) => <option key={b.brand_id} value={b.brand_id}>{b.name}</option>)}
                </select>
              )}
              <button onClick={aiCopy} disabled={!!busy} data-testid="dfy-flyer-aicopy" className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#1E5BA8] text-[#1E5BA8] text-sm font-bold hover:bg-[#EFF6FF] disabled:opacity-60">
                {busy === 'copy' ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Rewrite as flyer copy (AI)
              </button>
              {copy && ['headline', 'subhead', 'cta'].map((k) => (
                <input key={k} value={copy[k] || ''} onChange={(e) => setCopy({ ...copy, [k]: e.target.value })} placeholder={k} data-testid={`dfy-flyer-${k}`} className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm" />
              ))}
              {copy && <textarea value={copy.body || ''} onChange={(e) => setCopy({ ...copy, body: e.target.value })} rows={2} placeholder="body" data-testid="dfy-flyer-body" className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm" />}
              <div className="flex gap-2 items-center">
                <select value={bgStyle} onChange={(e) => setBgStyle(e.target.value)} data-testid="dfy-flyer-style" className="px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm">
                  {['photorealistic', 'cinematic', 'luxury', 'minimal', 'abstract'].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <button onClick={genBg} disabled={!!busy} data-testid="dfy-flyer-genbg" className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#7C3AED] text-white text-sm font-bold hover:bg-[#6D28D9] disabled:opacity-60">
                  {busy === 'bg' ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} {bgUrl ? 'Regenerate BG' : 'AI background'}
                </button>
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                {FLYER_TEMPLATES.map((t) => (
                  <button key={t.id} onClick={() => setTemplateId(t.id)} data-testid={`dfy-flyer-tpl-${t.id}`}
                    className={`px-2 py-1.5 rounded-lg border text-[11px] font-bold ${templateId === t.id ? 'border-[#1E5BA8] bg-[#EFF6FF] text-[#1E5BA8]' : 'border-[#E2E8F0] text-[#475569]'}`}>{t.name}</button>
                ))}
              </div>
              <div className="rounded-lg border border-[#E2E8F0] p-2.5" data-testid="dfy-flyer-qr">
                <label className="flex items-center gap-2 text-xs font-bold text-[#475569]"><input type="checkbox" checked={qr.enabled} onChange={(e) => setQr({ ...qr, enabled: e.target.checked })} data-testid="dfy-flyer-qr-enabled" /> Add branded QR code</label>
                {qr.enabled && (
                  <div className="mt-2 space-y-1.5">
                    <input value={qr.url} onChange={(e) => setQr({ ...qr, url: e.target.value })} placeholder="QR link e.g. https://calendly.com/you" data-testid="dfy-flyer-qr-url" className="w-full px-2 py-1.5 rounded-lg border border-[#E2E8F0] text-xs" />
                    <div className="flex gap-1.5">
                      <input value={qr.label} onChange={(e) => setQr({ ...qr, label: e.target.value })} placeholder="Caption" className="flex-1 px-2 py-1.5 rounded-lg border border-[#E2E8F0] text-xs" />
                      <select value={qr.position} onChange={(e) => setQr({ ...qr, position: e.target.value })} data-testid="dfy-flyer-qr-position" className="px-2 py-1.5 rounded-lg border border-[#E2E8F0] text-xs">
                        {[['bottom-right', 'BR'], ['bottom-left', 'BL'], ['top-right', 'TR'], ['top-left', 'TL'], ['center', 'Center']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div>
              <div className="mx-auto rounded-xl overflow-hidden shadow-lg" style={{ width: FLYER_W * scale, height: FLYER_H * scale }} data-testid="dfy-flyer-preview">
                <div style={{ transform: `scale(${scale})`, transformOrigin: 'top left', width: FLYER_W, height: FLYER_H }}>
                  <FlyerCanvas templateId={templateId} brand={brand} copy={copy || {}} backgroundUrl={bgUrl} qr={qr} />
                </div>
              </div>
              <button onClick={attach} disabled={!!busy || !copy} data-testid="dfy-flyer-attach" className="w-full mt-3 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#1E5BA8] text-white text-sm font-bold hover:bg-[#1A4F94] disabled:opacity-50">
                {busy === 'attach' ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Attach to post
              </button>
            </div>
          </div>
        )}
        <div style={{ position: 'fixed', left: -99999, top: 0, pointerEvents: 'none' }} aria-hidden>
          {brand && <FlyerCanvas innerRef={exportRef} templateId={templateId} brand={brand} copy={copy || {}} backgroundUrl={bgUrl} qr={qr} />}
        </div>
      </div>
    </div>
  );
}


export function DFYPlanGenerator({ api, profiles, onCreateBrand, memberView = false }) {
  const [profileId, setProfileId] = useState('');
  const [days, setDays] = useState(90);
  const [videosPerDay, setVideosPerDay] = useState(1);
  const [postsPerDay, setPostsPerDay] = useState(2);
  const [notes, setNotes] = useState('');
  const [creating, setCreating] = useState(false);
  const [filling, setFilling] = useState(false);
  const [progress, setProgress] = useState({ filled: 0, total: 0 });
  const [plan, setPlan] = useState(null);
  const [plans, setPlans] = useState([]);
  const [openDays, setOpenDays] = useState({});
  const [remixing, setRemixing] = useState({});
  const [viewFilter, setViewFilter] = useState('both'); // both | video | post
  const [shortMode, setShortMode] = useState({});       // {item_id: true => showing short}
  const [shortLoading, setShortLoading] = useState({});
  const [remixOpen, setRemixOpen] = useState({});       // {item_id: true => box open}
  const [remixText, setRemixText] = useState({});
  // Sharing a plan as a paid "starter kit"
  const [sharePlan, setSharePlan] = useState(null);          // plan being shared
  const [shareEmail, setShareEmail] = useState('');
  const [shareName, setShareName] = useState('');
  const [shareMsg, setShareMsg] = useState('');
  const [shareSending, setShareSending] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [accessPrice, setAccessPrice] = useState(null);
  const [editingPrice, setEditingPrice] = useState(false);
  const [priceInput, setPriceInput] = useState('');
  const [maxPlanDays, setMaxPlanDays] = useState(null); // null = unlimited (admin/pro)
  // AI media (image / video) generation
  const [mediaConfirm, setMediaConfirm] = useState(null); // { item, kind: 'image' | 'video' }
  const [flyerItem, setFlyerItem] = useState(null);       // post item being made into a branded flyer
  const [imgLoading, setImgLoading] = useState({});       // {item_id: true}
  const videoPolls = useRef({});

  useEffect(() => () => { Object.values(videoPolls.current).forEach((iv) => clearInterval(iv)); }, []);

  const updateItem = useCallback((id, patch) => {
    setPlan((cur) => (cur ? { ...cur, items: cur.items.map((x) => (x.item_id === id ? { ...x, ...patch } : x)) } : cur));
  }, []);

  const startVideoPoll = useCallback((planId, itemId) => {
    if (videoPolls.current[itemId]) clearInterval(videoPolls.current[itemId]);
    videoPolls.current[itemId] = setInterval(async () => {
      try {
        const r = await api.get(`/api/admin/social-studio/content-plans/${planId}`);
        const fresh = (r.data.plan?.items || []).find((x) => x.item_id === itemId);
        if (!fresh) return;
        if (fresh.video_status === 'ready' || fresh.video_status === 'failed') {
          clearInterval(videoPolls.current[itemId]); delete videoPolls.current[itemId];
          updateItem(itemId, { video_status: fresh.video_status, video_url: fresh.video_url, video_error: fresh.video_error });
          if (fresh.video_status === 'ready') toast.success('AI video is ready!');
          else toast.error(fresh.video_error || 'Video generation failed');
        }
      } catch { /* keep polling */ }
    }, 8000);
  }, [api, updateItem]);

  const runImage = useCallback(async (it) => {
    setImgLoading((s) => ({ ...s, [it.item_id]: true }));
    try {
      const r = await api.post(`/api/admin/social-studio/content-plans/${plan.plan_id}/items/${it.item_id}/generate-image`, { style: 'photorealistic', aspect_hint: 'portrait' });
      updateItem(it.item_id, { image_url: r.data.image_url, image_public_id: r.data.image_public_id, image_status: 'ready' });
      toast.success('AI image generated');
    } catch (e) {
      toast.error(apiErr(e, 'Image generation failed'));
    } finally {
      setImgLoading((s) => ({ ...s, [it.item_id]: false }));
    }
  }, [api, plan, updateItem]);

  const runVideo = useCallback(async (it) => {
    updateItem(it.item_id, { video_status: 'generating', video_error: null });
    try {
      await api.post(`/api/admin/social-studio/content-plans/${plan.plan_id}/items/${it.item_id}/generate-video`, { duration: 8 });
      startVideoPoll(plan.plan_id, it.item_id);
      toast.message('Generating your AI video… this takes ~2–5 minutes.');
    } catch (e) {
      updateItem(it.item_id, { video_status: 'failed', video_error: apiErr(e, 'Could not start video generation') });
      toast.error(apiErr(e, 'Could not start video generation'));
    }
  }, [api, plan, updateItem, startVideoPoll]);

  const confirmMedia = useCallback(() => {
    if (!mediaConfirm) return;
    const { item, kind } = mediaConfirm;
    setMediaConfirm(null);
    if (kind === 'image') runImage(item);
    else runVideo(item);
  }, [mediaConfirm, runImage, runVideo]);

  // Batch media generation (whole day / whole plan)
  const [batchModal, setBatchModal] = useState(null); // { day: number | null }
  const [batchKinds, setBatchKinds] = useState('both');
  const [batchSkip, setBatchSkip] = useState(true);
  const batchPoll = useRef(null);

  const stopBatchPoll = useCallback(() => {
    if (batchPoll.current) { clearInterval(batchPoll.current); batchPoll.current = null; }
  }, []);

  const startBatchPoll = useCallback((planId) => {
    stopBatchPoll();
    batchPoll.current = setInterval(async () => {
      try {
        const r = await api.get(`/api/admin/social-studio/content-plans/${planId}`);
        setPlan(r.data.plan);
        const st = r.data.plan?.media_batch?.status;
        if (st !== 'running') {
          stopBatchPoll();
          if (st === 'done') toast.success('All media generated!');
          else if (st === 'paused') toast.error(r.data.plan?.media_batch?.error || 'Batch paused — credits low');
          else if (st === 'stopped') toast.message('Batch stopped');
        }
      } catch { /* keep polling */ }
    }, 6000);
  }, [api, stopBatchPoll]);

  useEffect(() => () => stopBatchPoll(), [stopBatchPoll]);

  const startBatch = useCallback(async () => {
    if (!plan || !batchModal) return;
    const { day } = batchModal;
    setBatchModal(null);
    try {
      const r = await api.post(`/api/admin/social-studio/content-plans/${plan.plan_id}/generate-media`, {
        day, kinds: batchKinds, skip_existing: batchSkip,
      });
      toast.message(`Generating ${r.data.total} media item${r.data.total === 1 ? '' : 's'}… you can keep working.`);
      const fresh = await api.get(`/api/admin/social-studio/content-plans/${plan.plan_id}`);
      setPlan(fresh.data.plan);
      startBatchPoll(plan.plan_id);
    } catch (e) {
      toast.error(apiErr(e, 'Could not start batch'));
    }
  }, [api, plan, batchModal, batchKinds, batchSkip, startBatchPoll]);

  const stopBatch = useCallback(async () => {
    if (!plan) return;
    try {
      await api.post(`/api/admin/social-studio/content-plans/${plan.plan_id}/generate-media/stop`);
      stopBatchPoll();
      const fresh = await api.get(`/api/admin/social-studio/content-plans/${plan.plan_id}`);
      setPlan(fresh.data.plan);
    } catch (e) { toast.error('Could not stop'); }
  }, [api, plan, stopBatchPoll]);

  const resumeBatch = useCallback(async () => {
    if (!plan) return;
    try {
      await api.post(`/api/admin/social-studio/content-plans/${plan.plan_id}/generate-media`, {});
      startBatchPoll(plan.plan_id);
      toast.message('Resuming…');
    } catch (e) { toast.error(apiErr(e, 'Could not resume')); }
  }, [api, plan, startBatchPoll]);

  // BYOK + usage + AI-credits banner + media gallery
  const [byokOpen, setByokOpen] = useState(false);
  const [usage, setUsage] = useState(null);
  const [aiLow, setAiLow] = useState(false);
  const [gallery, setGallery] = useState(null); // { loading, media, count, images, videos }

  const refreshUsageAndStatus = useCallback(async () => {
    try {
      const [u, s] = await Promise.all([
        api.get('/api/admin/social-studio/usage'),
        api.get('/api/admin/social-studio/ai-status'),
      ]);
      setUsage(u.data);
      setAiLow(!!s.data?.low);
    } catch { /* non-fatal */ }
  }, [api]);

  const dismissAiLow = useCallback(async () => {
    setAiLow(false);
    try { await api.post('/api/admin/social-studio/ai-status/dismiss'); } catch { /* ignore */ }
  }, [api]);

  const openGallery = useCallback(async () => {
    if (!plan) return;
    setGallery({ loading: true, media: [] });
    try {
      const r = await api.get(`/api/admin/social-studio/content-plans/${plan.plan_id}/media`);
      setGallery({ loading: false, ...r.data });
    } catch (e) {
      setGallery(null);
      toast.error('Could not load gallery');
    }
  }, [api, plan]);

  const downloadAllZip = useCallback(async () => {
    if (!plan) return;
    try {
      const r = await api.get(`/api/admin/social-studio/content-plans/${plan.plan_id}/media.zip`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${(plan.name || 'content-plan').replace(/\s+/g, '-')}-media.zip`;
      a.click(); URL.revokeObjectURL(url);
      toast.success('Downloading all media…');
    } catch (e) { toast.error(apiErr(e, 'Nothing to download yet')); }
  }, [api, plan]);

  useEffect(() => {
    if (plan?.media_batch?.status === 'paused') setAiLow(true);
  }, [plan?.media_batch?.status]);

  useEffect(() => {
    api.get('/api/admin/social-studio/usage')
      .then((r) => {
        const md = r.data?.tier === 'admin' ? null : (r.data?.plan?.max_content_plan_days ?? null);
        setMaxPlanDays(md);
        if (md != null) setDays((d) => (d > md ? md : d));
      })
      .catch(() => { /* non-fatal */ });
  }, [api]);

  const loadPrice = useCallback(async () => {
    try {
      const r = await api.get('/api/admin/social-studio/access-price');
      setAccessPrice(r.data.price_usd);
    } catch { /* non-fatal */ }
  }, [api]);
  useEffect(() => { loadPrice(); }, [loadPrice]);

  const savePrice = async () => {
    const v = parseFloat(priceInput);
    if (isNaN(v) || v < 0.5) { toast.error('Enter a valid price (min $0.50)'); return; }
    try {
      const r = await api.post('/api/admin/social-studio/access-price', { price_usd: v });
      setAccessPrice(r.data.price_usd);
      setEditingPrice(false);
      toast.success(`Studio Access price set to $${r.data.price_usd.toFixed(2)}/mo`);
    } catch (e) {
      toast.error('Could not save price: ' + (e.response?.data?.detail || e.message));
    }
  };

  const openShare = (p) => { setSharePlan(p); setShareEmail(''); setShareName(''); setShareMsg(''); setShareLink(''); };

  const sendShare = async () => {
    const email = shareEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) { toast.error('Enter a valid recipient email'); return; }
    setShareSending(true);
    try {
      const r = await api.post(`/api/admin/social-studio/content-plans/${sharePlan.plan_id}/share`, {
        recipient_email: email, recipient_name: shareName.trim(), message: shareMsg.trim(),
      });
      setShareLink(r.data.accept_url || '');
      toast.success(`Invite emailed to ${email}`);
    } catch (e) {
      toast.error('Share failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setShareSending(false);
    }
  };

  const refreshPlans = useCallback(async () => {
    try {
      const r = await api.get('/api/admin/social-studio/content-plans');
      setPlans((r.data.plans || []).filter((p) => p.kind === 'dfy'));
    } catch (e) { /* non-fatal */ }
  }, [api]);

  useEffect(() => { refreshPlans(); }, [refreshPlans]);

  const runFillLoop = useCallback(async (planId) => {
    setFilling(true);
    try {
      let done = false;
      let guard = 0;
      while (!done && guard < 400) {
        guard += 1;
        const r = await api.post(`/api/admin/social-studio/content-plans/${planId}/fill-next?batch=5`);
        const d = r.data;
        setProgress({ filled: d.filled, total: d.total });
        done = d.done;
        if (guard % 3 === 0 || done) {
          const detail = await api.get(`/api/admin/social-studio/content-plans/${planId}`);
          setPlan(detail.data.plan);
        }
      }
      toast.success('Your done-for-you plan is ready!');
      refreshPlans();
    } catch (e) {
      toast.error('Generation paused: ' + (e.response?.data?.detail || e.message) + '. Click Resume to continue.');
    } finally {
      setFilling(false);
    }
  }, [api, refreshPlans]);

  const handleCreate = async () => {
    if (!profileId) { toast.error('Pick a brand profile first'); return; }
    if (videosPerDay + postsPerDay < 1) { toast.error('Pick at least 1 video or post per day'); return; }
    setCreating(true);
    setPlan(null);
    setProgress({ filled: 0, total: days * (videosPerDay + postsPerDay) });
    try {
      const r = await api.post('/api/admin/social-studio/content-plans/dfy', {
        profile_id: profileId, days, videos_per_day: videosPerDay, posts_per_day: postsPerDay, extra_notes: notes,
      });
      setPlan(r.data.plan);
      toast.message(`Skeleton built (${r.data.plan.generation.total} pieces). Writing content…`);
      await runFillLoop(r.data.plan_id);
    } catch (e) {
      toast.error('Could not start plan: ' + (e.response?.data?.detail || e.message));
    } finally {
      setCreating(false);
    }
  };

  const openPlan = async (planId) => {
    try {
      const r = await api.get(`/api/admin/social-studio/content-plans/${planId}`);
      setPlan(r.data.plan);
      const g = r.data.plan.generation || {};
      setProgress({ filled: g.filled || 0, total: g.total || 0 });
      setOpenDays({ 1: true });
      setShortMode({}); setRemixOpen({}); setRemixText({});
      // Resume polling for any video still generating
      (r.data.plan.items || []).forEach((it) => {
        if (it.video_status === 'generating') startVideoPoll(planId, it.item_id);
      });
      if (r.data.plan.media_batch?.status === 'running') startBatchPoll(planId);
      refreshUsageAndStatus();
    } catch (e) { toast.error('Could not open plan'); }
  };

  const resume = async () => { if (plan) await runFillLoop(plan.plan_id); };

  const deletePlan = async (planId) => {
    try {
      await api.delete(`/api/admin/social-studio/content-plans/${planId}`);
      if (plan?.plan_id === planId) setPlan(null);
      refreshPlans();
      toast.success('Deleted');
    } catch (e) { toast.error('Delete failed'); }
  };

  const remixItem = async (itemId) => {
    if (!plan) return;
    setRemixing((r) => ({ ...r, [itemId]: true }));
    try {
      const instruction = (remixText[itemId] || '').trim();
      const res = await api.post(`/api/admin/social-studio/content-plans/${plan.plan_id}/items/${itemId}/remix`, { instruction });
      const updated = res.data.item;
      setPlan((cur) => ({ ...cur, items: cur.items.map((it) => (it.item_id === itemId ? updated : it)) }));
      setShortMode((m) => ({ ...m, [itemId]: false }));
      setRemixOpen((o) => ({ ...o, [itemId]: false }));
      toast.success('Remixed');
    } catch (e) {
      toast.error('Remix failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setRemixing((r) => ({ ...r, [itemId]: false }));
    }
  };

  const toggleShort = async (it) => {
    const id = it.item_id;
    if (shortMode[id]) { setShortMode((m) => ({ ...m, [id]: false })); return; }
    if (it.short_post) { setShortMode((m) => ({ ...m, [id]: true })); return; }
    setShortLoading((s) => ({ ...s, [id]: true }));
    try {
      const r = await api.post(`/api/admin/social-studio/content-plans/${plan.plan_id}/items/${id}/shorten`);
      setPlan((cur) => ({ ...cur, items: cur.items.map((x) => (x.item_id === id ? { ...x, short_post: r.data.short_post } : x)) }));
      setShortMode((m) => ({ ...m, [id]: true }));
      toast.success('Short version ready');
    } catch (e) {
      toast.error('Shorten failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setShortLoading((s) => ({ ...s, [id]: false }));
    }
  };

  const shortenDay = async (dn) => {
    if (!plan) return;
    setShortLoading((s) => ({ ...s, [`day-${dn}`]: true }));
    try {
      const r = await api.post(`/api/admin/social-studio/content-plans/${plan.plan_id}/days/${dn}/shorten-posts`);
      const shortened = r.data.shortened || {};
      const ids = Object.keys(shortened);
      if (ids.length === 0) { toast.message('All posts in this day are already shortened'); }
      else {
        setPlan((cur) => ({ ...cur, items: cur.items.map((x) => (shortened[x.item_id] ? { ...x, short_post: shortened[x.item_id] } : x)) }));
        setShortMode((m) => { const n = { ...m }; ids.forEach((id) => { n[id] = true; }); return n; });
        toast.success(`Shortened ${ids.length} post${ids.length > 1 ? 's' : ''} in Day ${dn}`);
      }
    } catch (e) {
      toast.error('Shorten all failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setShortLoading((s) => ({ ...s, [`day-${dn}`]: false }));
    }
  };

  const copy = (text, label) => { navigator.clipboard.writeText(text || ''); toast.success(`${label} copied`); };

  const pct = progress.total ? Math.round((progress.filled / progress.total) * 100) : 0;
  const busy = creating || filling;

  const itemsByDay = {};
  (plan?.items || []).forEach((it) => { (itemsByDay[it.day] = itemsByDay[it.day] || []).push(it); });
  const dayNums = Object.keys(itemsByDay).map(Number).sort((a, b) => a - b);
  const perDay = videosPerDay + postsPerDay;
  const matchesFilter = (it) => viewFilter === 'both' || (viewFilter === 'video' ? it.format === 'video' : it.format !== 'video');

  return (
    <div className="bg-gradient-to-br from-[#0A0F1C] to-[#1E293B] rounded-2xl p-6 text-white" data-testid="dfy-generator">
      <div className="flex items-center gap-2 mb-1">
        <CalendarRange size={18} className="text-[#F97316]" aria-hidden="true" />
        <h2 className="text-sm font-bold uppercase tracking-wider">Done-For-You Content Plan</h2>
      </div>
      <p className="text-xs text-[#94A3B8] mb-4 max-w-2xl">
        Pre-write up to <strong className="text-white">90 days</strong> of content: short-form video scripts +
        ready-to-paste posts, on a wide variety of topics, grounded in your reference library. Remix any piece
        (with your own instructions), shorten any post, and filter to just videos or just posts.
      </p>

      {((profiles || []).length === 0 && plans.length === 0 && !plan && !busy) ? (
        <div className="rounded-xl border border-[#F97316]/40 bg-[#F97316]/10 p-4 mb-4" data-testid="dfy-need-brand">
          <p className="text-sm font-bold text-white mb-1">Step 1: Create your brand profile</p>
          <p className="text-xs text-[#CBD5E1] mb-3">Your content plans are written in your brand's voice and grounded in your offers. Add a brand profile first, then come back here to generate your plan.</p>
          <button type="button" onClick={() => onCreateBrand && onCreateBrand()} data-testid="dfy-create-brand-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#F97316] text-white text-sm font-bold rounded-lg hover:bg-[#ea6a0f] transition-colors">
            <UserPlus size={15} aria-hidden="true" /> Create a brand profile
          </button>
        </div>
      ) : (plans.length === 0 && !plan && !busy) && (
        <div className="rounded-xl border border-white/15 bg-white/5 p-4 mb-4" data-testid="dfy-first-plan">
          <p className="text-sm font-bold text-white mb-1">Create your first content plan</p>
          <p className="text-xs text-[#CBD5E1]">Pick a brand profile below, choose how many days and how many posts/videos per day, then hit Generate. We'll write the whole calendar for you, piece by piece.</p>
        </div>
      )}

      <div className="grid md:grid-cols-4 gap-3 mb-3">
        <select value={profileId} onChange={(e) => setProfileId(e.target.value)} disabled={busy}
          aria-label="Brand profile" data-testid="dfy-profile"
          className="px-3 py-2 rounded-lg text-sm bg-white/10 border border-white/15 text-white focus:outline-none focus:border-[#F97316] md:col-span-2">
          <option value="" className="text-black">- Pick brand profile -</option>
          {(profiles || []).map((p) => <option key={p.profile_id} value={p.profile_id} className="text-black">{p.name}</option>)}
        </select>
        <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))} disabled={busy}
          aria-label="Plan length" data-testid="dfy-days"
          className="px-3 py-2 rounded-lg text-sm bg-white/10 border border-white/15 text-white focus:outline-none focus:border-[#F97316]">
          {[7, 14, 30, 60, 90].map((d) => {
            const locked = maxPlanDays != null && d > maxPlanDays;
            return (
              <option key={d} value={d} disabled={locked} className="text-black">
                {d} days{locked ? ' (Pro)' : ''}
              </option>
            );
          })}
        </select>
        <div className="grid grid-cols-2 gap-2">
          <select value={videosPerDay} onChange={(e) => setVideosPerDay(parseInt(e.target.value, 10))} disabled={busy}
            aria-label="Videos per day" data-testid="dfy-videos-perday"
            className="px-2 py-2 rounded-lg text-sm bg-white/10 border border-white/15 text-white focus:outline-none focus:border-[#F97316]">
            {[0, 1, 2, 3].map((n) => <option key={n} value={n} className="text-black">{n} video/day</option>)}
          </select>
          <select value={postsPerDay} onChange={(e) => setPostsPerDay(parseInt(e.target.value, 10))} disabled={busy}
            aria-label="Posts per day" data-testid="dfy-posts-perday"
            className="px-2 py-2 rounded-lg text-sm bg-white/10 border border-white/15 text-white focus:outline-none focus:border-[#F97316]">
            {[0, 1, 2, 3, 4].map((n) => <option key={n} value={n} className="text-black">{n} post/day</option>)}
          </select>
        </div>
      </div>
      <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} disabled={busy}
        placeholder="Optional themes/offers (e.g. 'lead with peace of mind, soft CTA to book a call')"
        aria-label="Plan notes" data-testid="dfy-notes"
        className="w-full px-3 py-2 rounded-lg text-sm bg-white/10 border border-white/15 text-white placeholder-[#64748B] focus:outline-none focus:border-[#F97316] mb-3" />

      <div className="flex items-center gap-3 flex-wrap">
        <button type="button" onClick={handleCreate} disabled={busy || !profileId} data-testid="dfy-generate-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#F97316] text-white text-sm font-bold rounded-lg hover:bg-[#ea6a0f] transition-colors disabled:opacity-50">
          {busy ? <Loader2 size={15} className="animate-spin" aria-hidden="true" /> : <Sparkles size={15} aria-hidden="true" />}
          {creating ? 'Building…' : filling ? 'Writing content…' : `Generate ${days}-Day Plan`}
        </button>
        {plan && progress.filled < progress.total && !filling && (
          <button type="button" onClick={resume} data-testid="dfy-resume-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white/10 text-white text-sm font-semibold rounded-lg hover:bg-white/20">
            Resume
          </button>
        )}
        <span className="text-xs text-[#94A3B8]">~{days * perDay} pieces • {videosPerDay}/day video + {postsPerDay}/day post</span>
      </div>

      {(busy || (plan && progress.total > 0)) && (
        <div className="mt-4" data-testid="dfy-progress">
          <div className="flex justify-between text-[11px] text-[#94A3B8] mb-1">
            <span>{progress.filled} / {progress.total} written</span>
            <span>{pct}%</span>
          </div>
          <div className="h-2 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-[#F97316] transition-all duration-500" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}

      {plans.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="dfy-saved">
          <div className="flex flex-wrap gap-2">
            {plans.map((p) => (
              <span key={p.plan_id} className="inline-flex items-center gap-1.5 bg-white/10 rounded-full pl-3 pr-1.5 py-1 text-xs">
                <button type="button" onClick={() => openPlan(p.plan_id)} className="hover:underline" data-testid={`dfy-open-${p.plan_id}`}>
                  {p.name} ({p.generation?.filled || 0}/{p.generation?.total || 0})
                </button>
                <button type="button" onClick={() => openShare(p)} aria-label="Share plan" title="Share this plan with another agent" className="text-[#93C5FD] hover:text-white" data-testid={`dfy-share-${p.plan_id}`}>
                  <Send size={12} aria-hidden="true" />
                </button>
                <button type="button" onClick={() => deletePlan(p.plan_id)} aria-label="Delete plan" className="text-[#94A3B8] hover:text-red-400">
                  <Trash2 size={12} aria-hidden="true" />
                </button>
              </span>
            ))}
          </div>
          {/* Studio Access price (what recipients pay to claim a shared plan) - admin only */}
          {!memberView && (
          <div className="flex items-center gap-2 text-[11px] text-white/60" data-testid="dfy-access-price">
            <DollarSign size={12} aria-hidden="true" />
            {editingPrice ? (
              <span className="inline-flex items-center gap-1.5">
                <span>Recipients pay $</span>
                <input type="number" step="0.01" min="0.5" value={priceInput} onChange={(e) => setPriceInput(e.target.value)}
                  className="w-20 px-2 py-0.5 rounded bg-white/10 border border-white/20 text-white text-xs" aria-label="Access price" data-testid="dfy-price-input" />
                <span>/mo</span>
                <button type="button" onClick={savePrice} className="text-emerald-300 hover:text-emerald-200" data-testid="dfy-price-save"><Check size={13} aria-hidden="true" /></button>
                <button type="button" onClick={() => setEditingPrice(false)} className="text-white/50 hover:text-white"><X size={13} aria-hidden="true" /></button>
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5">
                <span>Shared plans unlock at <strong className="text-white/80">${(accessPrice ?? 10).toFixed(2)}/mo</strong></span>
                <button type="button" onClick={() => { setPriceInput(String(accessPrice ?? 10)); setEditingPrice(true); }} className="underline hover:text-white" data-testid="dfy-price-edit">edit</button>
              </span>
            )}
          </div>
          )}
        </div>
      )}

      {plan && dayNums.length > 0 && (
        <div className="mt-5 bg-white rounded-xl p-4 text-[#0A0F1C] max-h-[680px] overflow-auto" data-testid="dfy-viewer">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <h3 className="text-sm font-bold">{plan.name}</h3>
            <div className="flex items-center gap-2 flex-wrap">
              <button type="button" onClick={() => openGallery()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#E2E8F0] text-[#475569] text-xs font-bold hover:border-[#7C3AED] hover:text-[#7C3AED]"
                data-testid="dfy-gallery-btn">
                <ImageIcon size={13} aria-hidden="true" /> Media gallery
              </button>
              <button type="button" onClick={() => setByokOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#E2E8F0] text-[#475569] text-xs font-bold hover:border-[#0EA5E9] hover:text-[#0EA5E9]"
                data-testid="dfy-byok-btn">
                <KeyRound size={13} aria-hidden="true" /> API keys
              </button>
              {/* View filter */}
              <div className="inline-flex rounded-lg border border-[#E2E8F0] overflow-hidden text-xs font-semibold" data-testid="dfy-view-filter">
                {[['both', 'Both'], ['video', 'Videos'], ['post', 'Posts']].map(([val, label]) => (
                  <button key={val} type="button" onClick={() => setViewFilter(val)}
                    className={`px-3 py-1.5 transition-colors ${viewFilter === val ? 'bg-[#0A0F1C] text-white' : 'bg-white text-[#475569] hover:bg-[#F1F5F9]'}`}
                    data-testid={`dfy-filter-${val}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* AI credits low banner */}
          {aiLow && (
            <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-start justify-between gap-2" data-testid="dfy-ai-credits-banner">
              <span className="text-xs text-amber-800 inline-flex items-start gap-1.5">
                <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
                AI credits on your Universal Key are running low — top up at <strong className="mx-1">Profile → Universal Key → Add Balance</strong> to keep generating, or add your own API key.
              </span>
              <button type="button" onClick={dismissAiLow} className="text-amber-700 hover:text-amber-900 flex-shrink-0" data-testid="dfy-ai-credits-dismiss" aria-label="Dismiss"><X size={15} aria-hidden="true" /></button>
            </div>
          )}

          {/* Daily quota chips (members only) */}
          {usage && !usage.unlimited && (
            <div className="mb-3 flex items-center gap-2 flex-wrap text-[11px]" data-testid="dfy-quota-chips">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#F0F9FF] text-[#0369A1] font-semibold">
                <ImageIcon size={11} aria-hidden="true" /> {usage.remaining?.ai_images ?? '∞'} images left today
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#F5F3FF] text-[#7C3AED] font-semibold">
                <Video size={11} aria-hidden="true" /> {usage.remaining?.ai_videos ?? '∞'} videos left today
              </span>
            </div>
          )}

          {/* Batch progress banner */}
          {plan.media_batch && ['running', 'paused'].includes(plan.media_batch.status) && (() => {
            const mb = plan.media_batch;
            const processed = (mb.done || 0) + (mb.failed || 0);
            const bpct = mb.total ? Math.round((processed / mb.total) * 100) : 0;
            const paused = mb.status === 'paused';
            return (
              <div className={`mb-3 rounded-lg border p-3 ${paused ? 'border-amber-300 bg-amber-50' : 'border-[#DDD6FE] bg-[#F5F3FF]'}`} data-testid="dfy-batch-progress">
                <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
                  <span className="text-xs font-bold inline-flex items-center gap-1.5 text-[#0A0F1C]">
                    {paused ? <AlertTriangle size={13} className="text-amber-600" aria-hidden="true" /> : <Loader2 size={13} className="animate-spin text-[#7C3AED]" aria-hidden="true" />}
                    {mb.scope} · {mb.kinds} · {processed}/{mb.total} done{mb.failed ? ` (${mb.failed} failed)` : ''}
                  </span>
                  <div className="flex items-center gap-2">
                    {paused ? (
                      <button type="button" onClick={resumeBatch} className="inline-flex items-center gap-1 text-[11px] font-bold text-[#7C3AED] hover:underline" data-testid="dfy-batch-resume">
                        <Play size={12} aria-hidden="true" /> Resume
                      </button>
                    ) : (
                      <button type="button" onClick={stopBatch} className="inline-flex items-center gap-1 text-[11px] font-bold text-[#DC2626] hover:underline" data-testid="dfy-batch-stop">
                        <Square size={11} aria-hidden="true" /> Stop
                      </button>
                    )}
                  </div>
                </div>
                {paused && mb.error && <p className="text-[11px] text-amber-800 mb-1.5">{mb.error}</p>}
                <div className="h-1.5 rounded-full bg-black/10 overflow-hidden">
                  <div className={`h-full transition-all duration-500 ${paused ? 'bg-amber-500' : 'bg-[#7C3AED]'}`} style={{ width: `${bpct}%` }} />
                </div>
              </div>
            );
          })()}
          <div className="space-y-2">
            {dayNums.map((dn) => {
              const all = itemsByDay[dn];
              const items = all.filter(matchesFilter);
              if (items.length === 0) return null;
              const isOpen = openDays[dn];
              const vids = items.filter((i) => i.format === 'video').length;
              const posts = items.filter((i) => i.format !== 'video').length;
              return (
                <div key={dn} className="border border-[#E2E8F0] rounded-lg">
                  <div className="flex items-center">
                    <button type="button" onClick={() => setOpenDays((o) => ({ ...o, [dn]: !o[dn] }))}
                      className="flex-1 flex items-center justify-between px-3 py-2 text-sm font-semibold hover:bg-[#F8FAFC]"
                      data-testid={`dfy-day-${dn}`}>
                      <span>Day {dn} <span className="text-[#94A3B8] font-normal">· {vids} video · {posts} post</span></span>
                      {isOpen ? <ChevronDown size={15} aria-hidden="true" /> : <ChevronRight size={15} aria-hidden="true" />}
                    </button>
                    {posts > 0 && viewFilter !== 'video' && (
                      <button type="button" onClick={() => shortenDay(dn)} disabled={shortLoading[`day-${dn}`]}
                        className="px-2 py-2 inline-flex items-center gap-1 text-[10px] font-semibold text-[#059669] hover:underline disabled:opacity-50 whitespace-nowrap"
                        data-testid={`dfy-shorten-day-${dn}`}>
                        {shortLoading[`day-${dn}`] ? <Loader2 size={11} className="animate-spin" aria-hidden="true" /> : <Minimize2 size={11} aria-hidden="true" />}
                        Shorten all posts
                      </button>
                    )}
                    <button type="button" onClick={() => setBatchModal({ day: dn })} disabled={plan.media_batch?.status === 'running'}
                      className="px-2 py-2 inline-flex items-center gap-1 text-[10px] font-semibold text-[#7C3AED] hover:underline disabled:opacity-50 whitespace-nowrap"
                      data-testid={`dfy-genmedia-day-${dn}`}>
                      <Wand2 size={11} aria-hidden="true" /> Generate media
                    </button>
                  </div>
                  {isOpen && (
                    <div className="px-3 pb-3 space-y-3">
                      {items.map((it) => {
                        const isVideo = it.format === 'video';
                        const fullBody = isVideo ? it.video_script : it.full_post;
                        const hasShort = !isVideo && !!it.short_post;
                        const showShort = hasShort && shortMode[it.item_id];
                        return (
                          <div key={it.item_id} className="border border-[#F1F5F9] rounded-lg p-3 bg-[#FAFBFC]" data-testid={`dfy-item-${it.item_id}`}>
                            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                              <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${isVideo ? 'text-[#7C3AED] bg-[#F5F3FF]' : 'text-[#1D4ED8] bg-[#EFF6FF]'}`}>
                                {isVideo ? <FileVideo size={11} aria-hidden="true" /> : <FileText size={11} aria-hidden="true" />}
                                {isVideo ? 'Video' : 'Post'}
                              </span>
                              <span className="text-[10px] font-bold uppercase tracking-wider text-[#475569] bg-[#F1F5F9] px-1.5 py-0.5 rounded">{it.pillar}</span>
                              <span className="text-xs font-semibold text-[#0A0F1C]">{it.title || it.theme}</span>
                            </div>
                            {!it.filled ? (
                              <p className="text-xs text-[#94A3B8] italic">Not written yet. Click Resume.</p>
                            ) : (
                              <>
                                <p className="text-xs text-[#475569] whitespace-pre-line mb-2">{fullBody}</p>
                                <div className="flex gap-3 flex-wrap items-center">
                                  <button type="button" onClick={() => copy(fullBody, isVideo ? 'Video script' : 'Post')}
                                    className={`inline-flex items-center gap-1 text-[11px] font-semibold hover:underline ${isVideo ? 'text-[#7C3AED]' : 'text-[#1D4ED8]'}`}
                                    data-testid={`dfy-copy-${it.item_id}`}>
                                    <Copy size={12} aria-hidden="true" /> Copy {isVideo ? 'script' : 'post'}
                                  </button>
                                  {!isVideo && (
                                    <button type="button" onClick={() => toggleShort(it)} disabled={shortLoading[it.item_id]}
                                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#059669] hover:underline disabled:opacity-50"
                                      data-testid={`dfy-short-${it.item_id}`}>
                                      {shortLoading[it.item_id] ? <Loader2 size={12} className="animate-spin" aria-hidden="true" /> : <Minimize2 size={12} aria-hidden="true" />}
                                      {shortLoading[it.item_id] ? 'Shortening…' : showShort ? 'Hide short' : (hasShort ? 'Show short' : 'Shorten')}
                                    </button>
                                  )}
                                  <button type="button" onClick={() => setRemixOpen((o) => ({ ...o, [it.item_id]: !o[it.item_id] }))}
                                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#F97316] hover:underline"
                                    data-testid={`dfy-remix-toggle-${it.item_id}`}>
                                    <Shuffle size={12} aria-hidden="true" /> Remix
                                  </button>
                                  {isVideo ? (
                                    <button type="button" onClick={() => setMediaConfirm({ item: it, kind: 'video' })}
                                      disabled={it.video_status === 'generating'}
                                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#7C3AED] hover:underline disabled:opacity-50"
                                      data-testid={`dfy-gen-video-${it.item_id}`}>
                                      {it.video_status === 'generating' ? <Loader2 size={12} className="animate-spin" aria-hidden="true" /> : <Video size={12} aria-hidden="true" />}
                                      {it.video_status === 'generating' ? 'Generating video…' : it.video_url ? 'Regenerate video' : 'Generate video'}
                                    </button>
                                  ) : (
                                    <button type="button" onClick={() => setMediaConfirm({ item: it, kind: 'image' })}
                                      disabled={imgLoading[it.item_id]}
                                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0EA5E9] hover:underline disabled:opacity-50"
                                      data-testid={`dfy-gen-image-${it.item_id}`}>
                                      {imgLoading[it.item_id] ? <Loader2 size={12} className="animate-spin" aria-hidden="true" /> : <ImageIcon size={12} aria-hidden="true" />}
                                      {imgLoading[it.item_id] ? 'Generating image…' : it.image_url ? 'Regenerate image' : 'Generate image'}
                                    </button>
                                  )}
                                  {!isVideo && (
                                    <button type="button" onClick={() => setFlyerItem(it)}
                                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#1E5BA8] hover:underline"
                                      data-testid={`dfy-flyer-${it.item_id}`}>
                                      <Wand2 size={12} aria-hidden="true" /> {it.image_is_flyer ? 'Edit flyer' : 'Branded flyer'}
                                    </button>
                                  )}
                                </div>
                                {showShort && (
                                  <div className="mt-2 border-l-2 border-[#10B981] bg-[#F0FDF4] rounded-r-md p-2.5" data-testid={`dfy-short-block-${it.item_id}`}>
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#059669]">Short version</span>
                                      <button type="button" onClick={() => copy(it.short_post, 'Short post')}
                                        className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#059669] hover:underline"
                                        data-testid={`dfy-copy-short-${it.item_id}`}>
                                        <Copy size={11} aria-hidden="true" /> Copy short
                                      </button>
                                    </div>
                                    <p className="text-xs text-[#475569] whitespace-pre-line">{it.short_post}</p>
                                  </div>
                                )}
                                {remixOpen[it.item_id] && (
                                  <div className="mt-2 bg-white border border-[#FED7AA] rounded-lg p-2.5" data-testid={`dfy-remix-box-${it.item_id}`}>
                                    <textarea
                                      value={remixText[it.item_id] || ''}
                                      onChange={(e) => setRemixText((t) => ({ ...t, [it.item_id]: e.target.value }))}
                                      placeholder="Optional: tell the AI what to change, e.g. 'Change the CTA to call me at 912-376-5544', 'make the hook a question', 'shift the angle to protecting grandkids'..."
                                      rows={2}
                                      className="w-full text-xs px-2 py-1.5 border border-[#E2E8F0] rounded-md focus:outline-none focus:border-[#F97316] resize-y"
                                      data-testid={`dfy-remix-input-${it.item_id}`}
                                    />
                                    <div className="flex items-center gap-2 mt-2">
                                      <button type="button" onClick={() => remixItem(it.item_id)} disabled={remixing[it.item_id]}
                                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#F97316] text-white text-[11px] font-bold rounded-md hover:bg-[#ea6a0f] disabled:opacity-50"
                                        data-testid={`dfy-remix-apply-${it.item_id}`}>
                                        {remixing[it.item_id] ? <Loader2 size={12} className="animate-spin" aria-hidden="true" /> : <Shuffle size={12} aria-hidden="true" />}
                                        {remixing[it.item_id] ? 'Remixing…' : (remixText[it.item_id] || '').trim() ? 'Apply changes' : 'Remix (fresh take)'}
                                      </button>
                                      <span className="text-[10px] text-[#94A3B8]">Leave blank for a fresh take on the same theme</span>
                                    </div>
                                  </div>
                                )}
                                {/* Generated AI image */}
                                {it.image_url && (
                                  <div className="mt-2 rounded-lg overflow-hidden border border-[#E0F2FE] bg-[#F0F9FF] p-2" data-testid={`dfy-image-block-${it.item_id}`}>
                                    <div className="flex items-center justify-between mb-1.5">
                                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#0369A1] inline-flex items-center gap-1">{it.image_is_flyer ? <><Wand2 size={11} aria-hidden="true" /> Branded flyer</> : <><ImageIcon size={11} aria-hidden="true" /> AI image</>}</span>
                                      <div className="flex items-center gap-2.5">
                                        <button type="button" onClick={() => { localStorage.setItem('dca_flyer_import', JSON.stringify({ image_url: it.image_url, title: it.title || it.theme || 'Edited flyer', plan_id: plan.plan_id, item_id: it.item_id })); window.location.assign('/admin/flyer-studio'); }} className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#1E5BA8] hover:underline" data-testid={`dfy-edit-flyer-${it.item_id}`}><Wand2 size={11} aria-hidden="true" /> Edit in Flyer Studio</button>
                                        <a href={it.image_url} download target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#0369A1] hover:underline" data-testid={`dfy-image-download-${it.item_id}`}><Download size={11} aria-hidden="true" /> Download</a>
                                      </div>
                                    </div>
                                    <img src={it.image_url} alt={it.title || it.theme} className="rounded-md max-h-72 w-auto" data-testid={`dfy-image-${it.item_id}`} />
                                  </div>
                                )}
                                {/* Generated AI video */}
                                {it.video_status === 'generating' && (
                                  <div className="mt-2 rounded-lg border border-[#EDE9FE] bg-[#F5F3FF] p-3 inline-flex items-center gap-2 text-[11px] font-semibold text-[#7C3AED]" data-testid={`dfy-video-generating-${it.item_id}`}>
                                    <Loader2 size={13} className="animate-spin" aria-hidden="true" /> Generating AI video with Sora 2… (~2–5 min). You can keep working.
                                  </div>
                                )}
                                {it.video_status === 'failed' && it.video_error && (
                                  <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-[11px] text-amber-800 inline-flex items-start gap-1.5" data-testid={`dfy-video-failed-${it.item_id}`}>
                                    <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" aria-hidden="true" /> {it.video_error}
                                  </div>
                                )}
                                {it.video_url && it.video_status !== 'generating' && (
                                  <div className="mt-2 rounded-lg overflow-hidden border border-[#EDE9FE] bg-[#F5F3FF] p-2" data-testid={`dfy-video-block-${it.item_id}`}>
                                    <div className="flex items-center justify-between mb-1.5">
                                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#7C3AED] inline-flex items-center gap-1"><Video size={11} aria-hidden="true" /> AI video</span>
                                      <a href={it.video_url} download target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#7C3AED] hover:underline" data-testid={`dfy-video-download-${it.item_id}`}><Download size={11} aria-hidden="true" /> Download</a>
                                    </div>
                                    <video src={it.video_url} controls className="rounded-md max-h-80 w-auto" data-testid={`dfy-video-${it.item_id}`} />
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Share-a-plan modal */}
      {sharePlan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="dfy-share-modal" onClick={() => !shareSending && setSharePlan(null)}>
          <div className="bg-white text-[#0A0F1C] rounded-2xl w-full max-w-md p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-1">
              <h3 className="text-lg font-bold inline-flex items-center gap-2"><Send size={18} className="text-[#1D4ED8]" aria-hidden="true" /> Share this plan</h3>
              <button type="button" onClick={() => setSharePlan(null)} aria-label="Close" className="text-[#94A3B8] hover:text-[#0A0F1C]"><X size={18} aria-hidden="true" /></button>
            </div>
            <p className="text-xs text-[#64748B] mb-4">
              Send <strong>{sharePlan.name}</strong> as a complete starter kit (plan + brand profile + theme bank + references).
              They subscribe at <strong>${(accessPrice ?? 10).toFixed(2)}/mo</strong>, then it loads into their own account.
            </p>

            {shareLink ? (
              <div className="space-y-3" data-testid="dfy-share-success">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800 inline-flex items-start gap-2">
                  <Check size={16} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <span>Invite emailed to <strong>{shareEmail}</strong>. You can also copy the link below.</span>
                </div>
                <div className="flex items-center gap-2">
                  <input readOnly value={shareLink} className="flex-1 px-2.5 py-1.5 border border-[#E2E8F0] rounded-lg text-xs bg-[#F8FAFC]" data-testid="dfy-share-link" />
                  <button type="button" onClick={() => copy(shareLink, 'Invite link')} className="px-3 py-1.5 bg-[#1D4ED8] text-white rounded-lg text-xs font-semibold inline-flex items-center gap-1.5"><Copy size={13} aria-hidden="true" /> Copy</button>
                </div>
                <button type="button" onClick={() => setSharePlan(null)} className="w-full mt-1 px-4 py-2 bg-[#0A0F1C] text-white rounded-lg text-sm font-semibold">Done</button>
              </div>
            ) : (
              <div className="space-y-3">
                <input type="email" value={shareEmail} onChange={(e) => setShareEmail(e.target.value)} placeholder="Recipient email *"
                  className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:border-[#1D4ED8]" data-testid="dfy-share-email" />
                <input type="text" value={shareName} onChange={(e) => setShareName(e.target.value)} placeholder="Recipient name (optional)"
                  className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:border-[#1D4ED8]" data-testid="dfy-share-name" />
                <textarea value={shareMsg} onChange={(e) => setShareMsg(e.target.value)} rows={2} placeholder="Personal note (optional)"
                  className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:border-[#1D4ED8] resize-y" data-testid="dfy-share-message" />
                <button type="button" onClick={sendShare} disabled={shareSending}
                  className="w-full px-4 py-2.5 bg-[#F97316] hover:bg-[#FB923C] disabled:opacity-60 text-white rounded-lg text-sm font-bold inline-flex items-center justify-center gap-2" data-testid="dfy-share-send">
                  {shareSending ? <><Loader2 size={15} className="animate-spin" aria-hidden="true" /> Sending…</> : <><Send size={15} aria-hidden="true" /> Send invite</>}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {/* AI media confirmation modal (shows credit cost) */}
      {mediaConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="dfy-media-confirm-modal" onClick={() => setMediaConfirm(null)}>
          <div className="bg-white text-[#0A0F1C] rounded-2xl w-full max-w-md p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-1">
              <h3 className="text-lg font-bold inline-flex items-center gap-2">
                {mediaConfirm.kind === 'video' ? <Video size={18} className="text-[#7C3AED]" aria-hidden="true" /> : <ImageIcon size={18} className="text-[#0EA5E9]" aria-hidden="true" />}
                Generate AI {mediaConfirm.kind}
              </h3>
              <button type="button" onClick={() => setMediaConfirm(null)} aria-label="Close" className="text-[#94A3B8] hover:text-[#0A0F1C]"><X size={18} aria-hidden="true" /></button>
            </div>
            {mediaConfirm.kind === 'video' ? (
              <p className="text-sm text-[#475569] mb-4">
                This creates a short vertical video with <strong>Sora 2</strong> from this day's script.
                It takes about <strong>2–5 minutes</strong> and <strong>uses AI credits</strong> from your Universal Key
                (video costs more than text/images). You can keep working while it renders.
              </p>
            ) : (
              <p className="text-sm text-[#475569] mb-4">
                This creates a photorealistic post image with <strong>Gemini Nano Banana</strong> and
                <strong> uses a small amount of AI credits</strong> from your Universal Key. It's usually ready in a few seconds.
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setMediaConfirm(null)} className="px-4 py-2 rounded-lg text-sm font-semibold text-[#475569] hover:bg-[#F1F5F9]" data-testid="dfy-media-cancel">Cancel</button>
              <button type="button" onClick={confirmMedia} data-testid="dfy-media-confirm"
                className={`inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-bold text-white ${mediaConfirm.kind === 'video' ? 'bg-[#7C3AED] hover:bg-[#6D28D9]' : 'bg-[#0EA5E9] hover:bg-[#0284C7]'}`}>
                <Sparkles size={15} aria-hidden="true" /> Generate {mediaConfirm.kind}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Batch media options modal */}
      {batchModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="dfy-batch-modal" onClick={() => setBatchModal(null)}>
          <div className="bg-white text-[#0A0F1C] rounded-2xl w-full max-w-md p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-1">
              <h3 className="text-lg font-bold inline-flex items-center gap-2"><Wand2 size={18} className="text-[#7C3AED]" aria-hidden="true" /> Generate media — {batchModal.day ? `Day ${batchModal.day}` : 'whole plan'}</h3>
              <button type="button" onClick={() => setBatchModal(null)} aria-label="Close" className="text-[#94A3B8] hover:text-[#0A0F1C]"><X size={18} aria-hidden="true" /></button>
            </div>
            <p className="text-sm text-[#475569] mb-4">
              Auto-create media for every matching item{batchModal.day ? ' in this day' : ' across the whole plan'}:
              photorealistic <strong>images</strong> for posts (Gemini Nano Banana) and short vertical <strong>videos</strong> for video slots (Sora 2).
            </p>
            <label className="text-xs font-bold uppercase tracking-wide text-[#64748B]">What to generate</label>
            <div className="grid grid-cols-3 gap-2 mt-1.5 mb-3">
              {[['both', 'Both'], ['images', 'Images'], ['videos', 'Videos']].map(([val, label]) => (
                <button key={val} type="button" onClick={() => setBatchKinds(val)} data-testid={`dfy-batch-kind-${val}`}
                  className={`px-2 py-2 rounded-lg border text-xs font-bold transition-all ${batchKinds === val ? 'border-[#7C3AED] bg-[#F5F3FF] text-[#7C3AED]' : 'border-[#E2E8F0] text-[#475569] hover:border-[#7C3AED]'}`}>
                  {label}
                </button>
              ))}
            </div>
            <label className="flex items-center gap-2 text-xs text-[#475569] cursor-pointer mb-3">
              <input type="checkbox" checked={batchSkip} onChange={(e) => setBatchSkip(e.target.checked)} data-testid="dfy-batch-skip" />
              Skip items that already have media (only fill what's missing)
            </label>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-[11px] text-amber-800 inline-flex items-start gap-1.5 mb-4">
              <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
              <span>This <strong>uses AI credits</strong> from your Universal Key. Videos render one at a time (~2–5 min each), so a full-plan video batch can take a while — you can Stop or Resume anytime.</span>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setBatchModal(null)} className="px-4 py-2 rounded-lg text-sm font-semibold text-[#475569] hover:bg-[#F1F5F9]" data-testid="dfy-batch-cancel">Cancel</button>
              <button type="button" onClick={startBatch} data-testid="dfy-batch-start"
                className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-bold text-white bg-gradient-to-r from-[#7C3AED] to-[#0EA5E9] hover:opacity-90">
                <Sparkles size={15} aria-hidden="true" /> Start
              </button>
            </div>
          </div>
        </div>
      )}

      {/* BYOK modal */}
      {byokOpen && <ByokModal api={api} onClose={() => { setByokOpen(false); refreshUsageAndStatus(); }} />}

      {/* Branded flyer composer */}
      {flyerItem && plan && (
        <PostFlyerModal api={api} plan={plan} item={flyerItem}
          onClose={() => setFlyerItem(null)}
          onAttached={(id, url) => updateItem(id, { image_url: url, image_status: 'ready', image_is_flyer: true })} />
      )}

      {/* Media gallery modal */}
      {gallery && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="dfy-gallery-modal" onClick={() => setGallery(null)}>
          <div className="bg-white text-[#0A0F1C] rounded-2xl w-full max-w-4xl p-6 shadow-xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-lg font-bold inline-flex items-center gap-2"><ImageIcon size={18} className="text-[#7C3AED]" aria-hidden="true" /> Media gallery</h3>
                {!gallery.loading && <p className="text-xs text-[#64748B] mt-0.5">{gallery.images || 0} image{gallery.images === 1 ? '' : 's'} · {gallery.videos || 0} video{gallery.videos === 1 ? '' : 's'}</p>}
              </div>
              <div className="flex items-center gap-2">
                {!gallery.loading && (gallery.count || 0) > 0 && (
                  <button type="button" onClick={downloadAllZip} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#7C3AED] text-white text-xs font-bold hover:bg-[#6D28D9]" data-testid="dfy-gallery-zip">
                    <Download size={13} aria-hidden="true" /> Download all (ZIP)
                  </button>
                )}
                <button type="button" onClick={() => setGallery(null)} aria-label="Close" className="text-[#94A3B8] hover:text-[#0A0F1C]"><X size={18} aria-hidden="true" /></button>
              </div>
            </div>
            {gallery.loading ? (
              <div className="py-16 flex items-center justify-center text-[#94A3B8]"><Loader2 size={22} className="animate-spin" aria-hidden="true" /></div>
            ) : (gallery.count || 0) === 0 ? (
              <div className="py-16 text-center text-sm text-[#64748B]" data-testid="dfy-gallery-empty">No media yet. Use “Generate media” on a day to create images and videos.</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="dfy-gallery-grid">
                {gallery.media.map((m) => (
                  <div key={`${m.item_id}-${m.type}`} className="rounded-lg overflow-hidden border border-[#E2E8F0] bg-[#F8FAFC]" data-testid={`dfy-gallery-item-${m.item_id}-${m.type}`}>
                    <div className="aspect-[3/4] bg-black/5 flex items-center justify-center overflow-hidden">
                      {m.type === 'video'
                        ? <video src={m.url} controls className="w-full h-full object-cover" />
                        : <img src={m.url} alt={m.title} className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex items-center justify-between px-2 py-1.5">
                      <span className="text-[10px] font-bold text-[#64748B] inline-flex items-center gap-1">
                        {m.type === 'video' ? <Video size={10} aria-hidden="true" /> : <ImageIcon size={10} aria-hidden="true" />} Day {m.day}
                      </span>
                      <a href={m.url} download target="_blank" rel="noopener noreferrer" className="text-[#7C3AED]" aria-label="Download"><Download size={13} aria-hidden="true" /></a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default DFYPlanGenerator;
